import { useEffect, useState } from "react";
import L from "leaflet";

export const STALE_S = 15;
export const LOST_S = 120;

const ts = (p) => (p?.last_update ? new Date(p.last_update).getTime() : 0);

// Merge GPS updates keeping only the newest fix per unit — never step a unit backwards in time.
export function mergeGps(prev, incoming, replaceAll = false) {
  const byUnit = new Map(prev.map((p) => [p.mapped_unit_id, p]));
  const next = replaceAll ? new Map() : byUnit;
  incoming.forEach((p) => {
    const old = byUnit.get(p.mapped_unit_id);
    next.set(p.mapped_unit_id, old && ts(old) > ts(p) ? old : p);
  });
  return Array.from(next.values());
}

export function useNow(ms = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function ageSeconds(p, now) {
  return p?.last_update ? Math.max(0, (now - new Date(p.last_update).getTime()) / 1000) : Infinity;
}

export function gpsStatus(p, now) {
  const age = ageSeconds(p, now);
  return age <= STALE_S ? "live" : age <= LOST_S ? "stale" : "lost";
}

export function fmtDuration(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(r).padStart(2, "0")}s`;
  return `${r}s`;
}

export const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour12: false }) : "--:--:--");

export function unitLabel(p) {
  if (p.abbreviation) return p.abbreviation;
  if (p.icon_label) return p.icon_label;
  const parts = (p.mapped_unit_name || p.deviceName || "?").replace(/[-_]/g, " ").split(/\s+/);
  return parts.length >= 2 ? (parts[0].slice(0, 2) + parts[parts.length - 1]).toUpperCase() : parts[0].slice(0, 4).toUpperCase();
}

export const STATUS_COLORS = { moving: "#00BFFF", stopped: "#FFB000", stale: "#B8B8B8", lost: "#FF6B6B" };

export function motionState(p, now) {
  const st = gpsStatus(p, now);
  if (st !== "live") return st;
  return p.stopped ? "stopped" : "moving";
}

export function straightMiles(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null)) return null;
  const R = 3958.8, toR = Math.PI / 180;
  const dLat = (lat2 - lat1) * toR, dLng = (lng2 - lng1) * toR;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toR) * Math.cos(lat2 * toR) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const AMB_SVG = (fill) => `<svg width="30" height="20" viewBox="0 0 30 20"><path d="M1 4h17v11H1z" fill="${fill}" stroke="#000" stroke-width="1"/><path d="M18 7h6l4 4v4H18z" fill="${fill}" stroke="#000" stroke-width="1"/><path d="M20 8h3.5l2.5 3h-6z" fill="#0b1a26"/><rect x="7.5" y="6" width="3" height="7" fill="#E00"/><rect x="5.5" y="8" width="7" height="3" fill="#E00"/><circle cx="6" cy="16" r="2.6" fill="#111" stroke="#FFF" stroke-width="1"/><circle cx="23" cy="16" r="2.6" fill="#111" stroke="#FFF" stroke-width="1"/></svg>`;

export function makeVehicleIcon(p, now) {
  const state = motionState(p, now);
  const color = STATUS_COLORS[state];
  const label = unitLabel(p);
  let detail = "";
  if (state === "moving") detail = `<span style="color:#7CF">${Math.round(p.speed_mph || 0)} MPH</span>`;
  else if (state === "stopped") {
    const dur = p.stopped_since ? fmtDuration((now - new Date(p.stopped_since).getTime()) / 1000) : "";
    detail = `<span style="color:#FFB000">STOPPED ${dur}</span>${p.stop_address ? `<br/><span style="color:#DDD;font-weight:500">${p.stop_address}</span>` : ""}`;
  } else {
    detail = `<span style="color:#FF9E9E">${state === "stale" ? "STALE" : "NO SIGNAL"} · LAST ${fmtClock(p.last_update)}</span>`;
  }
  const arrow = state === "moving" && p.heading != null
    ? `<div style="position:absolute;left:11px;top:-11px;transform:rotate(${p.heading}deg);transform-origin:4px 21px;width:0;height:0;border-left:4px solid transparent;border-right:4px solid transparent;border-bottom:8px solid ${color}"></div>` : "";
  const html = `<div style="position:relative;display:flex;align-items:center;gap:4px;opacity:${state === "lost" ? 0.85 : 1}">
    ${arrow}<div style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.7))">${AMB_SVG(color)}</div>
    <div style="background:rgba(8,12,18,.88);border:1px solid ${color};border-radius:3px;padding:1px 5px;white-space:nowrap;font-family:'IBM Plex Mono',monospace;font-size:10px;font-weight:700;line-height:1.3;color:#FFF">
      <span>${label}</span> ${detail}</div></div>`;
  return new L.DivIcon({ html, className: "", iconSize: [30, 20], iconAnchor: [15, 10], popupAnchor: [0, -12] });
}
